import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, TrendingUp, Send, Clock, CheckCircle, Zap, Shield, Calendar, BarChart3, Mail, Activity, Target, Building2, Pencil, Copy, Plus, Upload, LayoutTemplate } from 'lucide-react';
import './CustomerDashboard.css';
import axiosConfig from '../../config/axiosConfig';
import { useWebSocketClientSafe } from '../../context/websocketContext';

const WhatsAppBusinessDashboard = () => {
  const navigate = useNavigate();
  const [showAccessToken, setShowAccessToken] = useState(false)
  const [hoveredSlice, setHoveredSlice] = useState(null);
  const [wabaDetailsData, setWabaDetailsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [templateCounts, setTemplateCounts] = useState(null);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [companySummary, setCompanySummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [walletBalance, setWalletBalance] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(true);

  // State for campaign analytics (new API)
  const [campaignAnalytics, setCampaignAnalytics] = useState(null);
  const [loadingCampaignAnalytics, setLoadingCampaignAnalytics] = useState(true);

  // Refund notification state
  const [refundNotification, setRefundNotification] = useState(null);

  // WebSocket for real-time refund events (safe â€” null when provider not mounted)
  const wsContext = useWebSocketClientSafe();
  const refundUpdates = wsContext?.refundUpdates || [];

  // State for recent campaigns (from API)
  const [recentCampaignsData, setRecentCampaignsData] = useState([]);
  const [recentStats, setRecentStats] = useState(null);
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);

  // State for period analytics (growth metrics)
  const [periodAnalytics, setPeriodAnalytics] = useState(null);
  const [loadingPeriodAnalytics, setLoadingPeriodAnalytics] = useState(true);

  // State for editable fields
  const [displayName, setDisplayName] = useState('');
  const [displayImage, setDisplayImage] = useState(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [tempName, setTempName] = useState('');
  const [imageError, setImageError] = useState(false);
  const fileInputRef = React.useRef(null);

  // insightsData removed — was entirely hardcoded and unused

  const mapPlanStatus = (entity) => {
    if (!entity || entity.has_active_plan === undefined) {
      return null;
    }

    return {
      has_active_plan: entity.has_active_plan,
      plan_name: entity.display_name || entity.plan_name || 'No Plan',
      billing_cycle: entity.billing_cycle,
      days_remaining: entity.days_remaining || 0,
      expires_at: entity.expires_at,
      email: entity.email || { limit: 0, used: 0, remaining: 0 },
      whatsapp: entity.whatsapp || { campaign_limit: 0, campaigns_used: 0, campaigns_remaining: 0, is_unlimited: false },
      currency: 'INR',
    };
  };

  const mapPeriodAnalytics = (responseData) => {
    if (!responseData || (responseData.statuscode !== 200 && responseData.status !== true)) {
      return null;
    }

    const analytics = responseData.entity?.analytics || responseData.data?.analytics || [];

    const thisMonth = analytics.find((p) => p.period === 'thismonth');
    const lastMonth = analytics.find((p) => p.period === 'lastmonth');
    const thisWeek = analytics.find((p) => p.period === 'thisweek');
    const lastWeek = analytics.find((p) => p.period === 'lastweek');

    let monthGrowth = 0;
    let weekGrowth = 0;
    let totalSent = 0;
    let totalDelivered = 0;

    if (thisMonth && lastMonth) {
      const thisMonthSent = parseInt(thisMonth.sent, 10) || 0;
      const lastMonthSent = parseInt(lastMonth.sent, 10) || 0;

      if (lastMonthSent > 0) {
        monthGrowth = (((thisMonthSent - lastMonthSent) / lastMonthSent) * 100).toFixed(1);
      }

      totalSent = thisMonthSent;
      totalDelivered = parseInt(thisMonth.delivered, 10) || 0;
    }

    if (thisWeek && lastWeek) {
      const thisWeekSent = parseInt(thisWeek.sent, 10) || 0;
      const lastWeekSent = parseInt(lastWeek.sent, 10) || 0;
      if (lastWeekSent > 0) {
        weekGrowth = (((thisWeekSent - lastWeekSent) / lastWeekSent) * 100).toFixed(1);
      }
    }

    return {
      monthGrowth: parseFloat(monthGrowth),
      weekGrowth: parseFloat(weekGrowth),
      totalSent,
      totalDelivered,
      deliveryRate: totalSent > 0 ? ((totalDelivered / totalSent) * 100).toFixed(1) : 0,
    };
  };

  // Fetch all dashboard data in parallel on mount
  useEffect(() => {
    let mounted = true;

    const fetchDashboardData = async () => {
      setLoading(true);
      setLoadingTemplates(true);
      setLoadingSummary(true);
      setLoadingBalance(true);
      setLoadingCampaigns(true);
      setLoadingCampaignAnalytics(true);
      setLoadingPeriodAnalytics(true);

      try {
        const customeruuid = sessionStorage.getItem('customeruuid');
        const userStr = sessionStorage.getItem('user');
        const user = userStr ? JSON.parse(userStr) : null;
        const companyid = user?.companyid;

        const requests = [
          customeruuid ? axiosConfig.post('/companywaba/details', { customeruuid }) : Promise.resolve(null),
          axiosConfig.post('/template/counts'),
          axiosConfig.get('/dashboard/company-summary'),
          axiosConfig.get('/wallet/plan-status'),
          companyid ? axiosConfig.post('/campaign/recent-campaigns', { companyid }) : Promise.resolve(null),
          companyid ? axiosConfig.post('/campaign/recent-stats', { companyid }) : Promise.resolve(null),
          axiosConfig.get('/dashboard/campaign-analytics'),
          axiosConfig.post('/campaign/analytics/periods'),
        ];

        const [
          wabaResult,
          templateCountsResult,
          companySummaryResult,
          walletResult,
          recentCampaignsResult,
          recentStatsResult,
          campaignAnalyticsResult,
          periodAnalyticsResult,
        ] = await Promise.allSettled(requests);

        if (!mounted) return;

        if (!customeruuid) {
          console.error('Customer UUID not found in session storage');
          setDisplayName('Business Account');
          setTempName('Business Account');
        } else if (wabaResult.status === 'fulfilled' && wabaResult.value?.data?.status === true && wabaResult.value?.data?.entity) {
          const wabaEntity = wabaResult.value.data.entity;
          setWabaDetailsData(wabaEntity);
          const businessName = wabaEntity.verified_name || wabaEntity.business_name || 'Business Account';
          setDisplayName(businessName);
          setTempName(businessName);
        } else {
          if (wabaResult.status === 'rejected') {
            console.error('Error fetching WABA details:', wabaResult.reason);
          }
          setDisplayName('Business Account');
          setTempName('Business Account');
        }

        if (templateCountsResult.status === 'fulfilled' && templateCountsResult.value?.data?.status === true && templateCountsResult.value?.data?.entity) {
          setTemplateCounts(templateCountsResult.value.data.entity);
        } else {
          if (templateCountsResult.status === 'rejected') {
            console.error('Error fetching template counts:', templateCountsResult.reason);
          } else {
            console.error('Template counts API error:', templateCountsResult.value?.data?.message);
          }
          setTemplateCounts(null);
        }

        if (companySummaryResult.status === 'fulfilled' && companySummaryResult.value?.data?.status === true && companySummaryResult.value?.data?.entity) {
          setCompanySummary(companySummaryResult.value.data.entity);
        } else {
          if (companySummaryResult.status === 'rejected') {
            console.error('Error fetching company summary:', companySummaryResult.reason);
          } else {
            console.error('Company summary API error:', companySummaryResult.value?.data?.message);
          }
          setCompanySummary(null);
        }

        if (walletResult.status === 'fulfilled') {
          setWalletBalance(mapPlanStatus(walletResult.value?.data?.entity));
        } else {
          console.error('Error fetching wallet/plan status:', walletResult.reason);
          setWalletBalance(null);
        }

        if (!companyid) {
          console.error('Company ID not found in session storage');
          setRecentCampaignsData([]);
          setRecentStats(null);
        } else {
          if (recentCampaignsResult.status === 'fulfilled' && recentCampaignsResult.value?.data?.status === true && Array.isArray(recentCampaignsResult.value?.data?.entity)) {
            const campaigns = recentCampaignsResult.value.data.entity.map((c) => {
              const totalRecipients = parseInt(c.total_recipients || c.totalRecipients || 0, 10) || 0;
              const sent = parseInt(c.sent || 0, 10) || 0;
              const delivered = parseInt(c.delivered || 0, 10) || 0;
              const seen = parseInt(c.seen || 0, 10) || 0;
              const failed = parseInt(c.failed || 0, 10) || 0;
              const sending = parseInt(c.sending || 0, 10) || 0;
              const success = parseInt(c.success || 0, 10) || 0;

              const totalProcessed = sent + delivered + seen + failed;

              let status;
              if (totalRecipients === 0) {
                status = 'draft';
              } else if (totalProcessed >= totalRecipients) {
                status = 'completed';
              } else if (sent > 0 || delivered > 0) {
                status = 'in-progress';
              } else if (sending > 0 && totalProcessed === 0) {
                status = 'scheduled';
              } else if (totalProcessed === 0 && sending === 0) {
                status = 'scheduled';
              } else {
                status = 'completed';
              }

              return {
                id: c.campaignid,
                name: c.campaignname,
                templateName: c.templatename || '',
                templateCategory: c.templatecategory || '',
                status,
                contacts: totalRecipients,
                sent,
                delivered,
                seen,
                failed,
                sending,
                success,
                successRate: c.success_rate || 0,
                date: c.transentrytime,
                updatedAt: c.transupdatetime,
              };
            });
            setRecentCampaignsData(campaigns);
          } else {
            if (recentCampaignsResult.status === 'rejected') {
              console.error('Error fetching recent campaigns:', recentCampaignsResult.reason);
            } else {
              console.error('Recent campaigns API error:', recentCampaignsResult.value?.data?.message);
            }
            setRecentCampaignsData([]);
          }

          if (recentStatsResult.status === 'fulfilled' && recentStatsResult.value?.data?.status === true && recentStatsResult.value?.data?.entity) {
            setRecentStats(recentStatsResult.value.data.entity);
          } else {
            if (recentStatsResult.status === 'rejected') {
              console.error('Error fetching recent stats:', recentStatsResult.reason);
            } else {
              console.error('Recent stats API error:', recentStatsResult.value?.data?.message);
            }
            setRecentStats(null);
          }
        }

        if (campaignAnalyticsResult.status === 'fulfilled' && campaignAnalyticsResult.value?.data?.status === true && campaignAnalyticsResult.value?.data?.entity) {
          setCampaignAnalytics(campaignAnalyticsResult.value.data.entity);
        } else {
          if (campaignAnalyticsResult.status === 'rejected') {
            console.error('Error fetching campaign analytics:', campaignAnalyticsResult.reason);
          }
          setCampaignAnalytics(null);
        }

        if (periodAnalyticsResult.status === 'fulfilled') {
          setPeriodAnalytics(mapPeriodAnalytics(periodAnalyticsResult.value?.data));
        } else {
          console.error('Error fetching period analytics:', periodAnalyticsResult.reason);
          setPeriodAnalytics(null);
        }
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        if (!mounted) return;
        setLoading(false);
        setLoadingTemplates(false);
        setLoadingSummary(false);
        setLoadingBalance(false);
        setLoadingCampaigns(false);
        setLoadingCampaignAnalytics(false);
        setLoadingPeriodAnalytics(false);
      }
    };

    fetchDashboardData();

    return () => {
      mounted = false;
    };
  }, []);

  // WebSocket: Listen for real-time refund events and show toast notification
  useEffect(() => {
    if (!refundUpdates || refundUpdates.length === 0) return;

    const latestRefund = refundUpdates[refundUpdates.length - 1];
    
    if (latestRefund && latestRefund.restoredCount > 0) {
      // Re-fetch plan status to update quota display
      (async () => {
        try {
          const res = await axiosConfig.get('/wallet/plan-status');
          const entity = res.data?.entity;
          if (entity) {
            setWalletBalance(prev => ({
              ...prev,
              email: entity.email || prev?.email,
              whatsapp: entity.whatsapp || prev?.whatsapp,
            }));
          }
        } catch { /* ignore */ }
      })();

      // Show quota restore notification toast
      setRefundNotification({
        restoredCount: latestRefund.restoredCount,
        channel: latestRefund.channel,
        campaignName: latestRefund.campaignName || 'Campaign',
        type: latestRefund.type,
        timestamp: latestRefund.timestamp
      });

      // Auto-dismiss notification after 8 seconds
      setTimeout(() => {
        setRefundNotification(null);
      }, 8000);
    }
  }, [refundUpdates]);

  // Derived data
  const wabaDetails = loading ? {
    appId: 'Loading...',
    accessToken: '',
    phoneNumberId: 'Loading...',
    wabaId: 'Loading...',
    verificationStatus: 'LOADING',
    qualityRating: 'Loading...'
  } : wabaDetailsData ? {
    appId: wabaDetailsData.customeruuid || 'N/A',
    accessToken: wabaDetailsData.access_token || '',
    phoneNumberId: wabaDetailsData.phone_number_id || 'N/A',
    wabaId: wabaDetailsData.waba_id || 'N/A',
    verificationStatus: wabaDetailsData.waba_status ? wabaDetailsData.waba_status.toUpperCase() : 'PENDING',
    qualityRating: wabaDetailsData.quality_rating || 'N/A'
  } : {
    appId: 'Not Available',
    accessToken: '',
    phoneNumberId: 'Not Available',
    wabaId: 'Not Available',
    verificationStatus: 'NOT_CONFIGURED',
    qualityRating: 'Not Available'
  };

  // Derive planDetails from walletBalance (plan-status API response)
  const planDetails = loadingBalance ? {
    name: 'Loading...',
    transactionDate: 'Loading...',
  } : walletBalance?.has_active_plan !== undefined ? {
    name: walletBalance.has_active_plan ? (walletBalance.plan_name || 'Active Plan') : 'No Plan Active',
    transactionDate: walletBalance.expires_at ? new Date(walletBalance.expires_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A',
    currency: 'INR',
    hasActivePlan: walletBalance.has_active_plan,
    billingCycle: walletBalance.billing_cycle,
    daysRemaining: walletBalance.days_remaining,
    email: walletBalance.email || { limit: 0, used: 0, remaining: 0 },
    whatsapp: walletBalance.whatsapp || { campaign_limit: 0, campaigns_used: 0, campaigns_remaining: 0, is_unlimited: false },
  } : {
    name: 'No Plan Active',
    transactionDate: 'Not Available',
  };

  const templateStats = templateCounts ? {
    approved: (templateCounts.marketing?.approved || 0) + (templateCounts.utility?.approved || 0) + (templateCounts.authentication?.approved || 0),
    pending: (templateCounts.marketing?.pending || 0) + (templateCounts.utility?.pending || 0) + (templateCounts.authentication?.pending || 0),
    rejected: (templateCounts.marketing?.rejected || 0) + (templateCounts.utility?.rejected || 0) + (templateCounts.authentication?.rejected || 0),
    marketing: (templateCounts.marketing?.approved || 0) + (templateCounts.marketing?.pending || 0) + (templateCounts.marketing?.rejected || 0),
    utility: (templateCounts.utility?.approved || 0) + (templateCounts.utility?.pending || 0) + (templateCounts.utility?.rejected || 0),
    authentication: (templateCounts.authentication?.approved || 0) + (templateCounts.authentication?.pending || 0) + (templateCounts.authentication?.rejected || 0),
  } : {
    approved: 0,
    pending: 0,
    rejected: 0,
    marketing: 0,
    utility: 0,
    authentication: 0,
  };

  const stats = campaignAnalytics ? {
    totalSent: campaignAnalytics.messages?.total || 0,
    successful: campaignAnalytics.messages?.delivered || 0,
    failed: campaignAnalytics.messages?.failed || 0,
    pending: campaignAnalytics.messages?.pending || 0,
    seen: campaignAnalytics.messages?.seen || 0,
    campaigns: campaignAnalytics.campaigns?.total || 0,
    campaignsSent: campaignAnalytics.campaigns?.sent || 0,
    campaignsDraft: campaignAnalytics.campaigns?.draft || 0,
    sentThisMonth: campaignAnalytics.campaigns?.sentThisMonth || 0,
    deliveryRate: campaignAnalytics.messages?.deliveryRate || 0,
    readRate: campaignAnalytics.messages?.readRate || 0,
    failureRate: campaignAnalytics.messages?.failureRate || 0,
  } : companySummary ? {
    totalSent: companySummary.messages?.sent || 0,
    successful: companySummary.messages?.delivered || 0,
    failed: companySummary.messages?.failed || 0,
    pending: 0,
    seen: 0,
    campaigns: companySummary.totalCampaign || 0,
    campaignsSent: 0,
    campaignsDraft: 0,
    sentThisMonth: 0,
    deliveryRate: 0,
    readRate: 0,
    failureRate: 0,
  } : {
    totalSent: 0,
    successful: 0,
    failed: 0,
    pending: 0,
    seen: 0,
    campaigns: 0,
    campaignsSent: 0,
    campaignsDraft: 0,
    sentThisMonth: 0,
    deliveryRate: 0,
    readRate: 0,
    failureRate: 0,
  };

  const recentCampaigns = React.useMemo(() => {
    if (!recentCampaignsData || recentCampaignsData.length === 0) {
      return [];
    }

    const liveCampaigns = recentCampaignsData.filter(c => c.status === 'in-progress');
    const completedCampaigns = recentCampaignsData.filter(c => c.status !== 'in-progress');

    if (liveCampaigns.length > 0) {
      const remainingSlots = Math.max(0, 5 - liveCampaigns.length);
      const completedToShow = completedCampaigns.slice(0, remainingSlots);
      return [...liveCampaigns, ...completedToShow];
    }

    return completedCampaigns.slice(0, 5);
  }, [recentCampaignsData]);

  const liveCampaignCount = React.useMemo(
    () => recentCampaignsData.filter((c) => c.status === 'in-progress').length,
    [recentCampaignsData]
  );
  const completedCampaignCount = React.useMemo(
    () => recentCampaignsData.filter((c) => c.status === 'completed').length,
    [recentCampaignsData]
  );

  const maskToken = (token) => {
    if (!token) return '';
    if (showAccessToken) {
      return token;
    }
    return `${token.substring(0, 12)}...${token.substring(token.length - 4)}`;
  };

  const getStatusClass = (status) => {
    const classes = {
      'completed': 'status-completed',
      'in-progress': 'status-progress',
      'scheduled': 'status-scheduled',
      'draft': 'status-scheduled',
      'failed': 'status-failed'
    };
    return classes[status] || 'status-default';
  };

  const sentMessages = Number(stats.totalSent || 0);
  const deliveredMessages = Number(stats.successful || 0);
  const failedMessages = Number(stats.failed || 0);
  const seenMessages = Number(stats.seen || 0);
  const pendingMessages = Number(stats.pending || 0);

  // Use delivery rate directly from API when available, otherwise calculate
  const deliveredPct = campaignAnalytics ? Number(stats.deliveryRate) : (sentMessages > 0 ? (deliveredMessages / sentMessages) * 100 : 0);
  const failedPct = campaignAnalytics ? Number(stats.failureRate) : (sentMessages > 0 ? (failedMessages / sentMessages) * 100 : 0);
  const pendingPct = sentMessages > 0 ? Math.max(100 - deliveredPct - failedPct, 0) : 0;

  const recentCampaignCount = liveCampaignCount || Number(recentStats?.campaigncount || recentStats?.activeCampaigns || 0);
  const recentRecipients = Number(recentStats?.total_recipients || 0);
  const recentSending = Number(recentStats?.sending || 0);

  const totalTemplates = templateStats.approved + templateStats.pending + templateStats.rejected;
  const templateApprovalRate = totalTemplates > 0 ? Math.round((templateStats.approved / totalTemplates) * 100) : 0;
  const templatePendingRate = totalTemplates > 0 ? Math.round((templateStats.pending / totalTemplates) * 100) : 0;
  const templateRejectedRate = totalTemplates > 0 ? Math.round((templateStats.rejected / totalTemplates) * 100) : 0;

  const qualityBadgeText = wabaDetailsData?.quality_rating
    ? `Quality ${String(wabaDetailsData.quality_rating).toUpperCase()}`
    : 'Quality pending';

  const queueHealth = recentRecipients > 0
    ? Math.max(0, Math.min(100, Math.round(((recentRecipients - recentSending) / recentRecipients) * 100)))
    : 0;
  
  return (
    <div className="da-dashboard">
      {/* Quota Restore Notification Toast */}
      {refundNotification && (
        <div className="da-toast">
          <div className="da-toast-icon">
            <span>✅</span>
          </div>
          <div className="da-toast-body">
            <div className="da-toast-title">Quota Restored!</div>
            <div className="da-toast-message">
              <strong>{refundNotification.restoredCount}</strong>{' '}
              {refundNotification.channel === 'email' ? 'email' : 'WhatsApp campaign'} quota restored for{' '}
              <strong>{refundNotification.campaignName}</strong>
            </div>
            <div className="da-toast-sub">Failed messages have been refunded to your plan quota</div>
          </div>
          <button className="da-toast-close" onClick={() => setRefundNotification(null)}>{'×'}</button>
        </div>
      )}

      {/* Main container */}
      <div className="da-main">
        {/* Left content column */}
        <div className="da-center">
          {/* Header */}
          <header className="da-header">
            <div>
              <h2 className="da-header-title">Dashboard</h2>
              <p className="da-header-sub">Real-time messaging performance</p>
            </div>
          </header>

          {/* Stats Grid */}
          <div className="da-stats-grid">

            {/* Delivery Rate */}
            <div className="da-stat-card da-stat-card-center">
              {loadingCampaignAnalytics ? (
                <div className="da-stat-skeleton">
                  <div className="da-skl" style={{ width: 96, height: 96, borderRadius: '50%' }} />
                  <div className="da-skl" style={{ width: 80, height: 12 }} />
                </div>
              ) : (
                <>
                  <div className="da-rate-wrap">
                    {(() => {
                      const dr = Number.isFinite(deliveredPct) ? Math.max(0, Math.min(100, deliveredPct)) : 0;
                      const circumference = 251.2;
                      const scoreOffset = circumference - (dr / 100) * circumference;
                      const strokeColor = dr >= 80 ? '#16a34a' : dr >= 50 ? '#f59e0b' : '#dc2626';
                      return (
                        <>
                          <svg className="da-rate-svg" viewBox="0 0 96 96">
                            <circle cx="48" cy="48" r="40" fill="transparent" stroke="#e8ebf5" strokeWidth="8" />
                            <circle
                              cx="48" cy="48" r="40"
                              fill="transparent"
                              stroke={strokeColor}
                              strokeWidth="8"
                              strokeDasharray={circumference}
                              strokeDashoffset={scoreOffset}
                              style={{ transition: 'stroke-dashoffset 1s ease' }}
                            />
                          </svg>
                          <span className="da-rate-label">{deliveredPct.toFixed(0)}%</span>
                        </>
                      );
                    })()}
                  </div>
                  <p className="da-stat-label" style={{ marginTop: 12, marginBottom: 0 }}>Delivery Rate</p>
                  <div className="da-mini-legend da-mini-legend-tight">
                    <span className="da-mini-chip da-mini-chip-ok">{deliveredMessages.toLocaleString('en-IN')} delivered</span>
                    <span className="da-mini-chip da-mini-chip-pending">{failedMessages.toLocaleString('en-IN')} failed</span>
                  </div>
                  <p className="da-rate-live-text">{sentMessages.toLocaleString('en-IN')} total sent</p>
                  {campaignAnalytics && (
                    <p className="da-rate-live-text" style={{ color: '#0036b0', marginTop: 2 }}>
                      {stats.readRate}% read rate
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Messages Sent */}
            <div className="da-stat-card">
              {loadingCampaignAnalytics ? (
                <div className="da-stat-skeleton">
                  <div className="da-skl" style={{ width: 100, height: 28 }} />
                  <div className="da-skl" style={{ width: '100%', height: 48 }} />
                </div>
              ) : (
                <>
                  <p className="da-stat-label">Messages Sent</p>
                  <span className="da-stat-value da-stat-value-primary">
                    {stats.totalSent >= 1000 ? `${(stats.totalSent / 1000).toFixed(1)}k` : stats.totalSent}
                  </span>
                  <div className="da-sparkline">
                    <div className="da-spark-bar" style={{ height: '50%', background: 'rgba(0,54,176,0.1)' }} />
                    <div className="da-spark-bar" style={{ height: '66%', background: 'rgba(0,54,176,0.2)' }} />
                    <div className="da-spark-bar" style={{ height: '33%', background: 'rgba(0,54,176,0.1)' }} />
                    <div className="da-spark-bar" style={{ height: '50%', background: 'rgba(0,54,176,0.4)' }} />
                    <div className="da-spark-bar" style={{ height: '80%', background: 'rgba(0,54,176,0.6)' }} />
                    <div className="da-spark-bar" style={{ height: '50%', background: 'rgba(0,54,176,0.3)' }} />
                    <div className="da-spark-bar" style={{ height: '100%', background: '#0036b0' }} />
                  </div>
                  <div className="da-stat-breakdown">
                    <span className="da-stat-ok">{stats.successful} delivered</span>
                    <span className="da-stat-fail">{stats.failed} failed</span>
                  </div>
                  {campaignAnalytics && (
                    <div className="da-stat-breakdown" style={{ marginTop: 4 }}>
                      <span style={{ fontSize: '0.72rem', color: '#0036b0' }}>{seenMessages.toLocaleString('en-IN')} seen</span>
                      <span style={{ fontSize: '0.72rem', color: '#64748b' }}>{pendingMessages.toLocaleString('en-IN')} pending</span>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Campaign Summary */}
            <div className="da-stat-card">
              {loadingCampaignAnalytics ? (
                <div className="da-stat-skeleton">
                  <div className="da-skl" style={{ width: 100, height: 28 }} />
                  <div className="da-skl" style={{ width: 140, height: 12 }} />
                </div>
              ) : (
                <>
                  <p className="da-stat-label">Campaigns</p>
                  <span className="da-stat-value da-stat-value-dark">{stats.campaigns}</span>
                  <div className="da-campaign-meta" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
                    {campaignAnalytics ? (
                      <>
                        <span className="da-badge da-badge-ok" style={{ fontSize: '0.7rem' }}>{stats.campaignsSent} sent</span>
                        {stats.campaignsDraft > 0 && (
                          <span className="da-badge da-badge-sched" style={{ fontSize: '0.7rem' }}>{stats.campaignsDraft} draft</span>
                        )}
                      </>
                    ) : (
                      <>
                        {recentCampaignCount > 0 && (
                          <span className="da-badge da-badge-live" style={{ fontSize: '0.7rem' }}>{recentCampaignCount} live</span>
                        )}
                        <span style={{ fontSize: '0.75rem', color: '#64748b' }}>{completedCampaignCount} completed</span>
                      </>
                    )}
                  </div>
                  <div className="da-campaign-bottom" style={{ marginTop: 'auto' }}>
                    {campaignAnalytics ? (
                      <>
                        <div className="da-wallet-bar" style={{ height: 4, marginBottom: 4 }}>
                          <div className="da-wallet-bar-fill" style={{
                            width: `${stats.campaigns > 0 ? Math.round((stats.campaignsSent / stats.campaigns) * 100) : 0}%`,
                            background: '#0036b0'
                          }} />
                        </div>
                        <span className="da-stat-sub-text">{stats.sentThisMonth} sent this month</span>
                      </>
                    ) : (
                      <>
                        {periodAnalytics && (
                          <div style={{ marginBottom: 6 }}>
                            <div className="da-wallet-bar" style={{ height: 4, marginBottom: 4 }}>
                              <div className="da-wallet-bar-fill" style={{
                                width: `${Math.min(100, completedCampaignCount > 0 && stats.campaigns > 0 ? Math.round((completedCampaignCount / stats.campaigns) * 100) : 0)}%`,
                                background: '#0036b0'
                              }} />
                            </div>
                          </div>
                        )}
                        <span className="da-stat-sub-text">
                          {periodAnalytics?.monthGrowth > 0
                            ? `↑ ${periodAnalytics.monthGrowth}% vs last month`
                            : periodAnalytics?.monthGrowth < 0
                            ? `↓ ${Math.abs(periodAnalytics.monthGrowth)}% vs last month`
                            : `${recentRecipients.toLocaleString('en-IN')} total recipients`}
                        </span>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Template Momentum */}
            <div className="da-stat-card">
              {loadingTemplates ? (
                <div className="da-stat-skeleton">
                  <div className="da-skl" style={{ width: 100, height: 28 }} />
                  <div className="da-skl" style={{ width: '100%', height: 8 }} />
                </div>
              ) : (
                <>
                  <p className="da-stat-label">Template Momentum</p>
                  <span className="da-stat-value da-stat-value-dark" style={{ fontSize: '1.9rem' }}>{templateApprovalRate}%</span>
                  <div className="da-wallet-bar">
                    <div className="da-wallet-bar-fill" style={{ width: `${Math.min(100, templateApprovalRate)}%`, background: '#16a34a' }} />
                  </div>
                  <div className="da-template-rows">
                    <span className="da-template-row"><b>{templateStats.approved}</b> approved</span>
                    <span className="da-template-row"><b>{templateStats.pending}</b> pending</span>
                    <span className="da-template-row da-template-row-rejected"><b>{templateStats.rejected}</b> rejected</span>
                  </div>
                  <p className="da-stat-sub-text">{templateRejectedRate}% rejected • {totalTemplates} total templates</p>
                </>
              )}
            </div>

          </div>

          {/* Account Details */}
          <section className="da-account">
            <div className="da-account-accent" />
            <div className="da-account-inner">
              <div className="da-account-head">
                <h3>Account Details</h3>
              </div>

              {loading ? (
                <div className="da-fields-grid">
                  {[0, 1, 2].map(i => (
                    <div className="da-field" key={i}>
                      <div className="da-skl" style={{ width: 80, height: 10, marginBottom: 6 }} />
                      <div className="da-skl" style={{ width: '100%', height: 42 }} />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="da-fields-grid">
                  <div className="da-field">
                    <label>Name</label>
                    <div className="da-field-box">
                      <span className="da-field-value da-field-value-name">{displayName || 'Business Account'}</span>
                      <button className="da-btn-copy" onClick={() => navigator.clipboard?.writeText(displayName || 'Business Account')}>
                        <Copy size={14} />
                      </button>
                    </div>
                  </div>
                  <div className="da-field">
                    <label>WABA ID</label>
                    <div className="da-field-box">
                      <span className="da-field-value da-mono">{wabaDetails.wabaId}</span>
                      <button className="da-btn-copy" onClick={() => navigator.clipboard?.writeText(wabaDetails.wabaId)}>
                        <Copy size={14} />
                      </button>
                    </div>
                  </div>
                  <div className="da-field">
                    <label>Phone Number ID</label>
                    <div className="da-field-box">
                      <span className="da-field-value da-mono">{wabaDetails.phoneNumberId}</span>
                      <button className="da-btn-copy" onClick={() => navigator.clipboard?.writeText(wabaDetails.phoneNumberId)}>
                        <Copy size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="da-account-hidden" aria-hidden="true">
                <div className="da-field">
                  <label>Access Token</label>
                  <div className="da-field-box">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                      <Shield size={14} className="da-field-lock-icon" />
                      <span className="da-field-token">{maskToken(wabaDetails.accessToken)}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="da-btn-copy" onClick={() => setShowAccessToken(!showAccessToken)}>
                        {showAccessToken ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                      <button className="da-btn-copy" onClick={() => navigator.clipboard?.writeText(wabaDetails.accessToken)}>
                        <Copy size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {wabaDetails.verificationStatus === 'NOT_CONFIGURED' && (
                <div className="da-alert-danger">
                  <span>{'\u26A0\uFE0F'}</span>
                  <p>WhatsApp Business Account not configured. Complete setup to start sending messages.</p>
                </div>
              )}
            </div>
          </section>

          {/* Recent Campaigns */}
          <section className="da-campaigns">
            <div className="da-table-wrap">
              <div className="da-section-head">
                <h3>Recent Campaigns</h3>
                <button className="da-link" onClick={() => navigate('/dashboard/broadcast')}>View All</button>
              </div>
              <table className="da-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Status</th>
                    <th>Recipients</th>
                    <th>Delivery %</th>
                    <th>Date</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingCampaigns ? (
                    [1, 2, 3].map(i => (
                      <tr key={i}>
                        <td><div className="da-skl" style={{ width: 120, height: 14 }} /></td>
                        <td><div className="da-skl" style={{ width: 60, height: 20, borderRadius: 9999 }} /></td>
                        <td><div className="da-skl" style={{ width: 40, height: 14 }} /></td>
                        <td><div className="da-skl" style={{ width: 80, height: 6, borderRadius: 3 }} /></td>
                        <td><div className="da-skl" style={{ width: 70, height: 14 }} /></td>
                        <td></td>
                      </tr>
                    ))
                  ) : recentCampaigns.length === 0 ? (
                    <tr>
                      <td colSpan="6">
                        <div className="da-empty">
                          <Mail size={36} />
                          <h4>No Recent Campaigns</h4>
                          <p>Your campaigns will appear here</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    recentCampaigns.map(campaign => {
                      const processed = campaign.status === 'in-progress'
                        ? (campaign.sent || 0) + (campaign.delivered || 0) + (campaign.seen || 0) + (campaign.failed || 0)
                        : campaign.success + campaign.failed;
                      const pct = campaign.contacts > 0 ? Math.round((processed / campaign.contacts) * 100) : 0;
                      const sClass = {
                        completed: 'ok',
                        'in-progress': 'live',
                        scheduled: 'sched',
                        draft: 'draft',
                        failed: 'fail'
                      }[campaign.status] || 'sched';
                      const sLabel = campaign.status === 'scheduled' ? 'NOT SENT'
                        : campaign.status === 'draft' ? 'DRAFT'
                        : campaign.status.replace('-', ' ').toUpperCase();

                      return (
                        <tr key={campaign.id} className={campaign.status === 'in-progress' ? 'da-row-live' : ''}>
                          <td>
                            <p className="da-camp-name">{campaign.name}</p>
                            <p className="da-camp-meta">
                              {campaign.templateCategory ? campaign.templateCategory.toUpperCase() : 'Campaign'} {'\u2022'} WhatsApp
                            </p>
                          </td>
                          <td><span className={`da-badge da-badge-${sClass}`}>{sLabel}</span></td>
                          <td><span className="da-mono" style={{ color: 'var(--da-outline)', fontSize: '0.875rem' }}>{campaign.contacts.toLocaleString()}</span></td>
                          <td>
                            {campaign.status === 'scheduled' || campaign.status === 'draft' ? (
                              <span className="da-muted">Pending...</span>
                            ) : (
                              <div className="da-prog">
                                <div className="da-prog-track">
                                  <div className={`da-prog-fill da-prog-${campaign.status === 'failed' ? 'fail' : campaign.status === 'in-progress' ? 'live' : 'ok'}`} style={{ width: `${pct}%` }} />
                                </div>
                                <span className="da-prog-pct">{pct}%</span>
                              </div>
                            )}
                          </td>
                          <td>
                            <span className="da-camp-date">
                              {campaign.date ? new Date(campaign.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '\u2014'}
                            </span>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <div className="da-row-actions">
                              <button className="da-btn-icon" title="Edit" onClick={() => navigate('/dashboard/broadcast')}><Pencil size={13} /></button>
                              <button className="da-btn-icon" title="Analytics" onClick={() => navigate('/dashboard/analytics')}><Activity size={13} /></button>
                              <button className="da-btn-icon" title="Copy name" onClick={() => navigator.clipboard?.writeText(campaign.name)}><Copy size={13} /></button>
                              <button className="da-btn-icon" title={campaign.date ? new Date(campaign.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : 'No date'} onClick={() => navigate('/dashboard/broadcast')}><Calendar size={13} /></button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </section>

        </div>

        {/* Right Sidebar */}
        <aside className="da-sidebar">

        {/* Quick Actions */}
        <div className="da-side-block">
          {wabaDetailsData && (
            <div className="da-waba-inline">
              <span className="da-waba-dot" />
              <span className="da-waba-connected-label">WABA Connected</span>
              <span className="da-waba-phone da-mono">{wabaDetails.phoneNumberId}</span>
              <span className="da-waba-quality-badge">{wabaDetails.qualityRating}</span>
            </div>
          )}
          <h4 className="da-side-label">Quick Actions</h4>
          <div className="da-actions">
              <button className="da-action-primary" onClick={() => navigate('/dashboard/broadcast')}>
                <span>New Campaign</span>
                <Plus size={18} />
              </button>
              <button className="da-action" onClick={() => navigate('/dashboard/contacts')}>
                <span>Import Contacts</span>
                <Upload size={18} />
              </button>
              <button className="da-action" onClick={() => navigate('/dashboard/templates')}>
                <span>Template Manager</span>
                <LayoutTemplate size={18} />
              </button>
            </div>
          </div>

          {/* Subscription Status */}
          <div className="da-side-block">
            <div className="da-side-head">
              <h4 className="da-side-label">Subscription Status</h4>
              <Zap size={14} className="da-side-icon" />
            </div>

            {loadingBalance ? (
              <div className="da-sub-skel">
                <div className="da-skl" style={{ width: 80, height: 80, borderRadius: '50%' }} />
                <div className="da-skl" style={{ width: 120, height: 14 }} />
              </div>
            ) : (
              <div className="da-sub-card">
                {(() => {
                  const subName = planDetails.name && planDetails.name !== 'No Plan Active' && planDetails.name !== 'No Transactions Yet' ? planDetails.name : 'No Plan Active';
                  const subDate = planDetails.transactionDate && planDetails.transactionDate !== 'Not Available' && planDetails.transactionDate !== 'N/A' ? planDetails.transactionDate : 'N/A';
                  const isActive = subName !== 'No Plan Active';
                  return (
                    <div className="da-sub-top">
                      <div className="da-sub-copy">
                        <p className="da-sub-kicker">Current Plan</p>
                        <h5 className="da-sub-plan">{subName}</h5>
                        <span className={`da-badge ${isActive ? 'da-badge-ok' : 'da-badge-fail'} da-sub-status`}>
                          {isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div className="da-sub-expiry">
                        <p className="da-sub-expiry-label">Expires</p>
                        <p className="da-sub-expiry-date">{subDate}</p>
                      </div>
                    </div>
                  );
                })()}

                <div className="da-sub-liquid-wrap">
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                      <span style={{ fontSize: 13, color: '#555' }}>📧 Email</span>
                      <span style={{ fontSize: 12, color: '#999', marginLeft: 'auto' }}>{(planDetails.email?.remaining || 0).toLocaleString('en-IN')} / {(planDetails.email?.limit || 0).toLocaleString('en-IN')}</span>
                    </div>
                    <div className="da-wallet-bar" style={{ height: 6, marginBottom: 10 }}>
                      <div className="da-wallet-bar-fill" style={{ width: `${planDetails.email?.limit > 0 ? Math.min(100, ((planDetails.email?.used || 0) / planDetails.email.limit) * 100) : 0}%` }} />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                      <span style={{ fontSize: 13, color: '#555' }}>💬 WhatsApp</span>
                      <span style={{ fontSize: 12, color: '#999', marginLeft: 'auto' }}>
                        {planDetails.whatsapp?.is_unlimited ? 'Unlimited' : `${(planDetails.whatsapp?.campaigns_remaining || 0)} / ${(planDetails.whatsapp?.campaign_limit || 0)}`}
                      </span>
                    </div>
                    {!planDetails.whatsapp?.is_unlimited && (
                      <div className="da-wallet-bar" style={{ height: 6 }}>
                        <div className="da-wallet-bar-fill" style={{ width: `${planDetails.whatsapp?.campaign_limit > 0 ? Math.min(100, ((planDetails.whatsapp?.campaigns_used || 0) / planDetails.whatsapp.campaign_limit) * 100) : 0}%` }} />
                      </div>
                    )}
                  </div>
                </div>
                <button className="da-sub-renew" onClick={() => navigate('/dashboard/pricing')}>{'View Plan'}</button>
              </div>
            )}

            {planDetails.name === 'No Plan Active' && (
              <div className="da-alert-info">
                <span>{'\ud83d\udca1'}</span>
                <p>No active plan. <span style={{ color: '#1890ff', cursor: 'pointer' }} onClick={() => navigate('/dashboard/pricing')}>Purchase a plan</span> to start.</p>
              </div>
            )}
          </div>

          {/* Templates */}
          <div className="da-side-block">
            <div className="da-side-head">
              <h4 className="da-side-label">Templates</h4>
              <Shield size={14} className="da-side-icon" />
            </div>

            {loadingTemplates ? (
              <div className="da-sub-skel">
                <div className="da-skl" style={{ width: 64, height: 64, borderRadius: '50%' }} />
                {[0, 1, 2].map(i => (
                  <div key={i} className="da-skl" style={{ width: '100%', height: 14, animationDelay: `${i * 60}ms` }} />
                ))}
              </div>
            ) : (
              <div className="da-tpl-section">
                <div className="da-tpl-chart">
                  <div className="da-tpl-donut-wrap">
                    <svg className="da-tpl-donut" viewBox="0 0 64 64">
                      {(() => {
                        const data = [
                          { label: 'Marketing', value: templateStats.marketing, color: '#3b82f6' },
                          { label: 'Utility', value: templateStats.utility, color: '#f59e0b' },
                          { label: 'Auth', value: templateStats.authentication, color: '#0036b0' }
                        ];
                        const total = data.reduce((s, d) => s + d.value, 0);
                        if (total === 0) return <circle cx="32" cy="32" r="28" fill="none" stroke="#edeef0" strokeWidth="8" />;
                        const C = 2 * Math.PI * 28;
                        let off = 0;
                        return data.map((d, i) => {
                          const dash = (d.value / total) * C;
                          const cur = off;
                          off += dash;
                          return (
                            <circle
                              key={i}
                              cx="32" cy="32" r="28"
                              fill="none"
                              stroke={d.color}
                              strokeWidth="8"
                              strokeDasharray={`${dash} ${C - dash}`}
                              strokeDashoffset={-cur}
                              style={{ cursor: 'pointer', transition: 'opacity .2s' }}
                              opacity={hoveredSlice !== null && hoveredSlice !== i ? 0.35 : 1}
                              onMouseEnter={() => setHoveredSlice(i)}
                              onMouseLeave={() => setHoveredSlice(null)}
                            />
                          );
                        });
                      })()}
                    </svg>
                    <div className="da-tpl-donut-center">
                      <Zap size={20} color="#0036b0" fill="#0036b0" />
                    </div>
                  </div>

                  <div className="da-tpl-legend">
                    {[
                      { label: 'Marketing', value: templateStats.marketing, color: '#3b82f6' },
                      { label: 'Utility', value: templateStats.utility, color: '#f59e0b' },
                      { label: 'Auth', value: templateStats.authentication, color: '#0036b0' }
                    ].map((d, i) => (
                      <div
                        key={i}
                        className="da-tpl-legend-row"
                        onMouseEnter={() => setHoveredSlice(i)}
                        onMouseLeave={() => setHoveredSlice(null)}
                      >
                        <div className="da-tpl-legend-left">
                          <span className="da-tpl-dot" style={{ background: d.color }} />
                          <span className="da-tpl-legend-name">{d.label}</span>
                        </div>
                        <span className="da-tpl-legend-val">{d.value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <button className="da-tpl-link" onClick={() => navigate('/dashboard/templates')}>
                  <span>Manage Templates</span>
                  <Activity size={12} />
                </button>
              </div>
            )}
          </div>

        </aside>
      </div>
    </div>
  );
};

export default WhatsAppBusinessDashboard;
